// ============================================================
// CuttingTab.jsx  —  Noble Enterprises (tape module)
//
// STEP 2 OF PRODUCTION: cores → finished rolls
//
// TWO ROUTES
//   Cutting    plain rolls, open stock, sellable to anyone
//   Printing   the customer's name on the tape, so that stock
//              can only ever go to them
//
// THE YIELD CHECK
//   expected = FLOOR(core width ÷ cut size) × cores used
// A 1220 mm core cut to 2" (50.8 mm) gives 24 rolls. The
// leftover strip is scrap, not a narrower roll — floor division,
// remainder to the kg bag.
//
// THE CORE POOL
// Cores are fungible within a group but not across groups:
// paper cannot be printed, and widths differ. So the operator
// picks a group, and FIFO decides which batches inside it get
// consumed.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. PoolCards     — what is available to cut
//   C. RunForm
//   D. RunDetailRow
//   E. CuttingTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, X, Check, Printer, Scissors, ChevronDown as Chevron } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  warning:    '#d97706',
  warningBg:  '#fef3c7',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
  purple:     '#7c3aed',
  purpleBg:   '#ede9fe',
};

const money  = (n) => `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const today  = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');
const mm2in  = (mm) => (Number(mm) / 25.4).toFixed(1);


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children, hint }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
    {hint && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>{hint}</div>}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};

// A few rolls short of expectation is trim. Double digits means
// something went wrong or the wrong product was picked.
const varianceBand = (pct) => {
  const loss = Math.abs(pct);
  if (loss < 4)  return { colour: C.success, bg: C.successBg };
  if (loss < 10) return { colour: C.warning, bg: C.warningBg };
  return { colour: C.danger, bg: C.dangerBg };
};

const RouteBadge = ({ type }) => {
  const printing = type === 'printing';
  return (
    <span style={{
      fontSize: '12px', fontWeight: '600', padding: '3px 11px',
      borderRadius: '11px', whiteSpace: 'nowrap',
      background: printing ? C.purpleBg : C.infoBg,
      color:      printing ? C.purple   : C.info,
      display: 'inline-flex', alignItems: 'center', gap: '5px',
    }}>
      {printing ? <Printer size={11} /> : <Scissors size={11} />}
      {printing ? 'Printing' : 'Cutting'}
    </span>
  );
};


// ============================================================
// B. POOL CARDS
//
// One card per core group. Clicking a card starts a run from it,
// which saves the operator having to match the group by hand.
// ============================================================

const PoolCards = ({ pool, onPick, activeKey }) => {
  if (pool.length === 0) {
    return (
      <div style={{
        padding: '14px 18px', background: C.infoBg, borderRadius: '10px',
        fontSize: '13px', color: C.info, marginBottom: '20px',
      }}>
        No cores in the pool. Record core production first.
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
      gap: '14px', marginBottom: '20px',
    }}>
      {pool.map(group => {
        const key = `${group.tape_type_id}-${group.core_width_mm}-${group.core_length_meters}`;
        const isActive = key === activeKey;

        return (
          <button
            key={key}
            onClick={() => onPick(group)}
            style={{
              textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
              background: isActive ? '#fffaf2' : C.card,
              border: `1px solid ${isActive ? C.brand : C.border}`,
              borderRadius: '10px', padding: '16px 18px',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'baseline', gap: '8px',
              marginBottom: '6px',
            }}>
              <span style={{ fontSize: '24px', fontWeight: '700', color: C.brand }}>
                {group.cores_available}
              </span>
              <span style={{ fontSize: '13px', color: C.textSoft }}>cores</span>
            </div>

            <div style={{ fontSize: '13px', fontWeight: '600', color: C.text }}>
              {group.tape_type_name}
            </div>
            <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '2px' }}>
              {Number(group.core_width_mm)} mm ({mm2in(group.core_width_mm)}") ·
              {' '}{Number(group.core_length_meters)} m
            </div>
            <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '6px' }}>
              {group.batch_count} batch{group.batch_count === 1 ? '' : 'es'} ·
              {' '}oldest {fmtDate(group.oldest_batch_date)}
              {!group.is_printable && ' · no printing'}
            </div>
          </button>
        );
      })}
    </div>
  );
};


// ============================================================
// C. RUN FORM
// ============================================================

const emptyForm = {
  run_type: 'cutting',
  tape_type_id: '', core_width_mm: '', core_length_meters: '',
  tape_type_name: '', is_printable: true, cores_available: 0,
  product_id: '', newProductSize: '', isNewProduct: false,
  customer_id: '', print_design: '',
  cores_used: '', rolls_produced: '', scrap_kg: '',
  run_date: today(), notes: '',
};

const RunForm = ({ form, setForm, products, customers, onSave, onCancel, saving }) => {
  const product = products.find(p => String(p.id) === String(form.product_id));

  const coreWidth = Number(form.core_width_mm || 0);
  const sizeMm    = form.isNewProduct
    ? Number(form.newProductSize || 0) * 25.4
    : Number(product?.size_mm || 0);

  const cores = Number(form.cores_used || 0);
  const rolls = Number(form.rolls_produced || 0);

  const rollsPerCore  = (coreWidth > 0 && sizeMm > 0) ? Math.floor(coreWidth / sizeMm) : 0;
  const expectedRolls = rollsPerCore * cores;
  const tooManyCores  = cores > Number(form.cores_available);
  const tooManyRolls  = expectedRolls > 0 && rolls > expectedRolls;

  const variancePct = expectedRolls > 0 ? ((rolls - expectedRolls) / expectedRolls) * 100 : 0;

  // The remainder strip that cannot make a full roll
  const leftoverMm = (coreWidth > 0 && sizeMm > 0) ? coreWidth - (rollsPerCore * sizeMm) : 0;

  const printingBlocked = form.run_type === 'printing' && !form.is_printable;

  return (
    <div style={{ padding: '20px 22px', background: C.rowAlt, borderBottom: `1px solid ${C.borderSoft}` }}>

      {/* Route */}
      <div style={{ marginBottom: '18px' }}>
        <label style={{
          display: 'block', fontSize: '12px', fontWeight: '600',
          color: C.textSoft, marginBottom: '8px',
        }}>
          Route
        </label>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {[
            ['cutting',  'Cutting',  Scissors, 'Plain rolls, open stock'],
            ['printing', 'Printing', Printer,  "Customer's name on the tape"],
          ].map(([value, label, Icon, hint]) => {
            const on = form.run_type === value;
            // Paper cores cannot be printed
            const disabled = value === 'printing' && !form.is_printable;

            return (
              <button
                key={value}
                type="button"
                disabled={disabled}
                onClick={() => setForm({
                  ...form, run_type: value,
                  customer_id: '', print_design: '',
                })}
                style={{
                  flex: '1 1 200px', textAlign: 'left', padding: '13px 16px',
                  borderRadius: '9px', fontFamily: 'inherit',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.45 : 1,
                  background: on ? '#fffaf2' : '#ffffff',
                  border: `1px solid ${on ? C.brand : C.border}`,
                }}
              >
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  fontSize: '14px', fontWeight: '700',
                  color: on ? C.brand : C.text,
                }}>
                  <Icon size={16} /> {label}
                </div>
                <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '3px' }}>
                  {disabled ? 'This tape type cannot be printed' : hint}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: '16px', marginBottom: '18px',
      }}>
        {/* Product — plain dropdown, or type a new size */}
        <Field
          label="Product size"
          hint={form.isNewProduct ? 'In inches, e.g. 2' : undefined}
        >
          <div style={{ display: 'flex', gap: '8px' }}>
            {form.isNewProduct ? (
              <TextInput
                autoFocus
                type="number" step="0.1"
                value={form.newProductSize}
                onChange={e => setForm({ ...form, newProductSize: e.target.value })}
                placeholder="2"
              />
            ) : (
              <select
                style={inputStyle}
                value={form.product_id}
                onChange={e => setForm({ ...form, product_id: e.target.value })}
              >
                <option value="">Select…</option>
                {products
                  .filter(p => String(p.tape_type_id) === String(form.tape_type_id))
                  .map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
              </select>
            )}
            <button
              type="button"
              onClick={() => setForm({
                ...form,
                isNewProduct: !form.isNewProduct,
                product_id: '', newProductSize: '',
              })}
              style={{
                flexShrink: 0, padding: '0 13px', borderRadius: '8px',
                border: `1px solid ${form.isNewProduct ? C.brand : C.border}`,
                background: form.isNewProduct ? C.brand : '#ffffff',
                color: form.isNewProduct ? '#ffffff' : C.textSoft,
                fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                fontFamily: 'inherit', whiteSpace: 'nowrap',
              }}
            >
              {form.isNewProduct ? 'Cancel' : '+ New'}
            </button>
          </div>
        </Field>

        <Field
          label="Cores used"
          hint={`${form.cores_available} available in this group`}
        >
          <TextInput
            type="number" min="1"
            value={form.cores_used}
            onChange={e => setForm({ ...form, cores_used: e.target.value })}
            placeholder="0"
          />
        </Field>

        <Field
          label="Rolls produced"
          hint={expectedRolls > 0 ? `${expectedRolls} expected` : undefined}
        >
          <TextInput
            type="number" min="0"
            value={form.rolls_produced}
            onChange={e => setForm({ ...form, rolls_produced: e.target.value })}
            placeholder="0"
          />
        </Field>

        <Field label="Waste collected (kg)">
          <TextInput
            type="number" step="0.01" min="0"
            value={form.scrap_kg}
            onChange={e => setForm({ ...form, scrap_kg: e.target.value })}
            placeholder="0"
          />
        </Field>

        <Field label="Run date">
          <TextInput
            type="date"
            max={today()}
            value={form.run_date}
            onChange={e => setForm({ ...form, run_date: e.target.value })}
          />
        </Field>

        {/* Printing only */}
        {form.run_type === 'printing' && (
          <>
            <Field label="Customer">
              <select
                style={inputStyle}
                value={form.customer_id}
                onChange={e => setForm({ ...form, customer_id: e.target.value })}
              >
                <option value="">Select customer…</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>

            <Field label="Print design" hint="What goes on the tape">
              <TextInput
                value={form.print_design}
                onChange={e => setForm({ ...form, print_design: e.target.value })}
                placeholder="e.g. Ahmed Store logo"
              />
            </Field>
          </>
        )}

        <Field label="Notes">
          <TextInput
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Optional"
          />
        </Field>
      </div>

      {/* Live yield */}
      {rollsPerCore > 0 && cores > 0 && (
        <div style={{
          background: '#ffffff',
          border: `1px solid ${(tooManyCores || tooManyRolls) ? C.danger : C.border}`,
          borderRadius: '8px', padding: '16px 18px', marginBottom: '18px',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: '16px',
          }}>
            {[
              ['Per core',  `${rollsPerCore} rolls`, C.textSoft],
              ['Expected',  expectedRolls,           C.text],
              ['Entered',   rolls || '—',            C.text],
              ['Short by',  expectedRolls - rolls > 0 ? expectedRolls - rolls : '—',
                            expectedRolls - rolls > 0 ? C.warning : C.textMuted],
            ].map(([label, value, colour]) => (
              <div key={label}>
                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                  {label}
                </div>
                <div style={{ fontSize: '17px', fontWeight: '700', color: colour, marginTop: '3px' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '14px', paddingTop: '12px',
            borderTop: `1px solid ${C.borderSoft}`,
            display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
            fontSize: '12px', color: C.textMuted,
          }}>
            <span>
              {coreWidth} mm ÷ {sizeMm.toFixed(1)} mm = {rollsPerCore} per core
              {leftoverMm >= 1 && ` · ${leftoverMm.toFixed(0)} mm strip to scrap`}
            </span>
            {rolls > 0 && (
              <span style={{
                marginLeft: 'auto', fontWeight: '700',
                padding: '3px 10px', borderRadius: '10px',
                background: varianceBand(variancePct).bg,
                color: varianceBand(variancePct).colour,
              }}>
                {variancePct > 0 ? '+' : ''}{variancePct.toFixed(0)}%
              </span>
            )}
          </div>

          {tooManyCores && (
            <div style={{
              marginTop: '12px', padding: '10px 13px', borderRadius: '7px',
              background: C.dangerBg, color: C.danger, fontSize: '13px',
            }}>
              Only {form.cores_available} core(s) available in this group.
            </div>
          )}

          {tooManyRolls && (
            <div style={{
              marginTop: '12px', padding: '10px 13px', borderRadius: '7px',
              background: C.dangerBg, color: C.danger, fontSize: '13px',
            }}>
              {cores} core(s) can give at most {expectedRolls} roll(s) at this size.
              Check the count or the product.
            </div>
          )}
        </div>
      )}

      {printingBlocked && (
        <div style={{
          background: C.dangerBg, color: C.danger, borderRadius: '8px',
          padding: '13px 16px', marginBottom: '18px', fontSize: '13px',
        }}>
          These cores are {form.tape_type_name}, which cannot be printed. Use the cutting route.
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <Btn onClick={onSave} disabled={saving || tooManyCores || tooManyRolls || printingBlocked}>
          <Check size={16} /> {saving ? 'Saving…' : 'Record run'}
        </Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
};


// ============================================================
// D. DETAIL ROW
//
// Shows which core batches this run consumed — the trace back
// to the jumbo roll and the vendor.
// ============================================================

const RunDetailRow = ({ runId, colSpan }) => {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API}/tape/production-runs/${runId}`, { headers })
      .then(res => setRun(res.data.data))
      .catch(() => setRun(null))
      .finally(() => setLoading(false));
  }, [runId]); // eslint-disable-line

  const cell = { padding: '9px 12px', fontSize: '13px', borderTop: `1px solid ${C.borderSoft}` };
  const head = {
    ...cell, background: '#ffffff', fontWeight: '600', color: C.textSoft,
    fontSize: '11px', textTransform: 'uppercase', borderTop: 'none',
  };

  return (
    <tr>
      <td colSpan={colSpan} style={{
        padding: '18px 22px', background: C.rowAlt,
        borderTop: `1px solid ${C.borderSoft}`,
      }}>
        {loading && <p style={{ margin: 0, color: C.textSoft, fontSize: '13px' }}>Loading…</p>}

        {!loading && run && (
          <>
            <div style={{
              fontSize: '12px', fontWeight: '600', color: C.textSoft,
              textTransform: 'uppercase', marginBottom: '10px',
            }}>
              Cores consumed
            </div>

            <div style={{
              border: `1px solid ${C.border}`, borderRadius: '8px',
              overflow: 'hidden', background: '#ffffff',
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
                  <thead>
                    <tr>
                      <th style={{ ...head, textAlign: 'left' }}>Batch</th>
                      <th style={{ ...head, textAlign: 'right' }}>Cores taken</th>
                      <th style={{ ...head, textAlign: 'left' }}>Made on</th>
                      <th style={{ ...head, textAlign: 'left' }}>From roll</th>
                      <th style={{ ...head, textAlign: 'left' }}>Vendor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {run.cores.map(c => (
                      <tr key={c.id}>
                        <td style={{ ...cell, fontWeight: '600' }}>{c.batch_number}</td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>{c.cores_taken}</td>
                        <td style={{ ...cell, color: C.textSoft }}>{fmtDate(c.batch_date)}</td>
                        <td style={cell}>{c.vendor_roll_number}</td>
                        <td style={{ ...cell, color: C.textSoft }}>{c.vendor_name}</td>
                      </tr>
                    ))}
                    {run.cores.length === 0 && (
                      <tr>
                        <td colSpan="5" style={{ ...cell, textAlign: 'center', color: C.textMuted, padding: '22px' }}>
                          No batch record
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {run.notes && (
              <div style={{ marginTop: '12px', fontSize: '13px', color: C.textSoft }}>
                <strong style={{ color: C.text }}>Notes:</strong> {run.notes}
              </div>
            )}
          </>
        )}

        {!loading && !run && (
          <p style={{ margin: 0, color: C.danger, fontSize: '13px' }}>Could not load this run.</p>
        )}
      </td>
    </tr>
  );
};


// ============================================================
// E. MAIN COMPONENT
// ============================================================

const CuttingTab = () => {
  const [runs, setRuns]           = useState([]);
  const [pool, setPool]           = useState([]);
  const [products, setProducts]   = useState([]);
  const [customers, setCustomers] = useState([]);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [form, setForm]           = useState(null);
  const [saving, setSaving]       = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [banner, setBanner]       = useState({ type: '', message: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const [r, p, pr, cu, s] = await Promise.all([
        axios.get(`${API}/tape/production-runs`, { headers }),
        axios.get(`${API}/tape/core-batches/pool`, { headers }),
        axios.get(`${API}/tape/products`, { headers }),
        axios.get(`${API}/tape/customers/active`, { headers }),
        axios.get(`${API}/tape/production-runs/summary`, { headers }),
      ]);
      setRuns(r.data.data || []);
      setPool(p.data.data || []);
      setProducts((pr.data.data || []).filter(x => x.is_active));
      setCustomers(cu.data.data || []);
      setSummary(s.data.data || null);
    } catch {
      notify('error', 'Could not load cutting. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Picking a pool card opens the form already pointed at that
  // group, so the operator never has to match it by hand.
  const pickGroup = (group) => {
    setForm({
      ...emptyForm,
      run_type: group.is_printable ? 'cutting' : 'cutting',
      tape_type_id:       String(group.tape_type_id),
      tape_type_name:     group.tape_type_name,
      is_printable:       group.is_printable,
      core_width_mm:      group.core_width_mm,
      core_length_meters: group.core_length_meters,
      cores_available:    group.cores_available,
    });
  };

  const activeGroupKey = form
    ? `${form.tape_type_id}-${form.core_width_mm}-${form.core_length_meters}`
    : null;

  const save = async () => {
    const f = form;

    if (f.isNewProduct ? !(Number(f.newProductSize) > 0) : !f.product_id) {
      return notify('error', 'Pick or add a product size');
    }
    if (!f.run_date) return notify('error', 'Run date is required');

    const cores = Number(f.cores_used);
    const rolls = Number(f.rolls_produced);

    if (!Number.isInteger(cores) || cores <= 0) {
      return notify('error', 'Cores used must be a whole number above 0');
    }
    if (!Number.isInteger(rolls) || rolls < 0) {
      return notify('error', 'Rolls produced must be a whole number');
    }
    if (f.run_type === 'printing') {
      if (!f.customer_id)          return notify('error', 'Pick the customer this print is for');
      if (!f.print_design.trim())  return notify('error', 'Enter what is being printed');
    }

    setSaving(true);
    try {
      // A new product size gets created first so the run has a
      // real id to reference.
      let productId = f.product_id;
      if (f.isNewProduct) {
        const size = Number(f.newProductSize);
        const res = await axios.post(`${API}/tape/products`, {
          name: `${size} Inch`,
          tape_type_id: f.tape_type_id,
          size_inches: size,
        }, { headers });
        productId = res.data.data.id;
      }

      await axios.post(`${API}/tape/production-runs`, {
        run_type:           f.run_type,
        product_id:         productId,
        customer_id:        f.run_type === 'printing' ? f.customer_id : null,
        print_design:       f.run_type === 'printing' ? f.print_design : null,
        tape_type_id:       f.tape_type_id,
        core_width_mm:      f.core_width_mm,
        core_length_meters: f.core_length_meters,
        cores_used:         cores,
        rolls_produced:     rolls,
        scrap_kg:           f.scrap_kg,
        run_date:           f.run_date,
        notes:              f.notes,
      }, { headers });

      notify('success', 'Production run recorded');
      setForm(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(
      `Delete run ${row.run_number}? The cores go back to the pool and the rolls come out of stock.`
    )) return;

    try {
      await axios.delete(`${API}/tape/production-runs/${row.id}`, { headers });
      notify('success', 'Run deleted');
      if (expandedId === row.id) setExpandedId(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  const totalCores = pool.reduce((sum, g) => sum + Number(g.cores_available), 0);

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      {/* Summary */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        {[
          ['Cores in pool',   totalCores, C.brand],
          ['Rolls this month', summary?.rolls_this_month ?? '—', C.text],
          ['Printing runs',   summary?.printing_runs ?? '—', C.purple],
          ['Cutting runs',    summary?.cutting_runs ?? '—', C.info],
        ].map(([label, value, colour]) => (
          <div key={label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '16px 18px',
          }}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '21px', fontWeight: '700', color: colour, marginTop: '4px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Pool */}
      <div style={{ marginBottom: '4px' }}>
        <h3 style={{ margin: '0 0 3px', fontSize: '15px', fontWeight: '700', color: C.text }}>
          Core pool
        </h3>
        <p style={{ margin: '0 0 12px', fontSize: '13px', color: C.textSoft }}>
          Click a group to cut from it. Oldest batches are used first.
        </p>
      </div>

      {!loading && (
        <PoolCards pool={pool} onPick={pickGroup} activeKey={activeGroupKey} />
      )}

      {/* Runs */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Production runs
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Click a run to see which core batches it consumed.
            </p>
          </div>

          {!form && (
            <Btn onClick={() => pool.length > 0 && pickGroup(pool[0])} disabled={pool.length === 0}>
              <Plus size={16} /> Record run
            </Btn>
          )}
        </div>

        {form && (
          <RunForm
            form={form}
            setForm={setForm}
            products={products}
            customers={customers}
            onSave={save}
            onCancel={() => setForm(null)}
            saving={saving}
          />
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Run</th>
                <th style={thStyle}>Route</th>
                <th style={thStyle}>Product</th>
                <th style={thStyle}>For</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Cores</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Rolls</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Variance</th>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="9" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && runs.map(row => {
                const isOpen   = expandedId === row.id;
                const expected = Number(row.expected_rolls);
                const actual   = Number(row.rolls_produced);
                const pct      = expected > 0 ? ((actual - expected) / expected) * 100 : 0;
                const band     = varianceBand(pct);

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => setExpandedId(isOpen ? null : row.id)}
                      style={{ cursor: 'pointer', background: isOpen ? C.rowAlt : 'transparent' }}
                    >
                      <td style={{ ...tdStyle, fontWeight: '700' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                          <Chevron
                            size={15}
                            style={{
                              color: C.textMuted,
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.15s',
                            }}
                          />
                          {row.run_number}
                        </span>
                      </td>
                      <td style={tdStyle}><RouteBadge type={row.run_type} /></td>
                      <td style={tdStyle}>
                        {row.product_name}
                        <div style={{ fontSize: '11px', color: C.textMuted }}>{row.tape_type_name}</div>
                      </td>
                      <td style={{ ...tdStyle, fontSize: '13px' }}>
                        {row.customer_name || (
                          <span style={{ color: C.textMuted }}>Open stock</span>
                        )}
                        {row.print_design && (
                          <div style={{ fontSize: '11px', color: C.textMuted }}>{row.print_design}</div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>{row.cores_used}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <span style={{ fontWeight: '700' }}>{actual}</span>
                        <span style={{ color: C.textMuted }}> / {expected}</span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <span style={{
                          fontWeight: '700', fontSize: '12px',
                          padding: '3px 9px', borderRadius: '10px',
                          background: band.bg, color: band.colour,
                        }}>
                          {pct > 0 ? '+' : ''}{pct.toFixed(0)}%
                        </span>
                      </td>
                      <td style={{ ...tdStyle, color: C.textSoft }}>{fmtDate(row.run_date)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <IconBtn
                          danger
                          title="Delete run"
                          onClick={(e) => { e.stopPropagation(); remove(row); }}
                        >
                          <Trash2 size={15} />
                        </IconBtn>
                      </td>
                    </tr>

                    {isOpen && <RunDetailRow runId={row.id} colSpan={9} />}
                  </React.Fragment>
                );
              })}

              {!loading && runs.length === 0 && (
                <tr>
                  <td colSpan="9" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No production runs yet. Pick a core group above to start one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CuttingTab;